import { Button, Card, Typography } from "@norican/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Microsoft } from "@/components/icons/microsoft";
import { NoricanLogo } from "@/components/norican-logo";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "HROps";

// Local dev can't complete a real Microsoft sign-in (Entra won't redirect back
// to localhost), so the dev server shows a login picker that hits the
// backend's `/auth/dev-login` instead — a genuine session, just without
// Microsoft. Production builds always show the Microsoft button; the backend
// refuses `/auth/dev-login` there regardless (see DEV_AUTH_BYPASS).
const SHOW_DEV_LOGIN = import.meta.env.DEV;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [devSigningIn, setDevSigningIn] = useState(false);

  const handleMicrosoftLogin = () => {
    logger.debug("[login] redirecting to Microsoft SSO");
    window.location.assign("/api/auth/sso/login");
  };

  const handleDevLogin = async (role: "user" | "admin") => {
    logger.debug("[login] dev sign-in as", role);
    setError("");
    setDevSigningIn(true);
    try {
      const response = await fetch(`/api/auth/dev-login?role=${role}`, { method: "POST" });
      if (!response.ok) {
        throw new Error(`dev-login responded ${response.status}`);
      }
      logger.debug("[login] dev sign-in succeeded → redirecting to /queue");
      navigate("/queue");
    } catch (err) {
      logger.debug("[login] dev sign-in failed:", err);
      setError("Local sign-in failed. Please try again.");
      setDevSigningIn(false);
    }
  };

  useEffect(() => {
    const ssoError = searchParams.get("error");
    if (!ssoError) {
      return;
    }
    logger.debug("[login] sso error:", ssoError);
    setError(
      ssoError === "domain_not_allowed"
        ? "Your account isn't allowed to sign in to this app."
        : ssoError === "no_role"
          ? "You are signed in to Microsoft, but your account holds no HROps role. Ask IT to assign User or Admin."
          : "Sign-in failed. Please try again.",
    );
  }, [searchParams]);

  return (
    <div className="bg-bg flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <Card.Header className="text-center">
          {/* The mark carries the "whose app is this" job on the one screen
              seen before sign-in, where there is no nav to carry it. */}
          <NoricanLogo height={22} className="mx-auto mb-3" />
          <Typography as="h1" variant="heading-2">
            {PROJECT_NAME}
          </Typography>
          <p className="text-fg-muted text-sm">Sign in to continue</p>
        </Card.Header>
        <Card.Body>
          {SHOW_DEV_LOGIN ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-center text-xs">
                Local dev — Microsoft sign-in only works on the deployed app. One button per HROps
                role, so the difference between them can be seen without an Entra assignment.
              </p>
              <Button
                type="button"
                disabled={devSigningIn}
                onClick={() => handleDevLogin("user")}
                fullWidth
              >
                Continue as User
              </Button>
              <Button
                type="button"
                disabled={devSigningIn}
                variant="outline"
                onClick={() => handleDevLogin("admin")}
                fullWidth
              >
                Continue as Admin
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              startIcon={<Microsoft className="size-4" />}
              fullWidth
            >
              Sign in with Microsoft
            </Button>
          )}
          {error && <p className="text-danger-fg mt-4 text-center text-sm">{error}</p>}
        </Card.Body>
      </Card>
    </div>
  );
}
